"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "motion/react";
import {
  Crown,
  Layers,
  Mic,
  Briefcase,
  BookOpen,
  Phone,
  ArrowUpRight,
  Menu,
  X,
} from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const NAV = [
  {
    label: "About",
    href: "/about/",
    icon: Crown,
    description: "The physician-turned-operator. A decade of sovereign-scale leadership.",
    sections: [
      { label: "Biography", href: "/about/", desc: "The full arc, from medicine to sovereign-scale operations." },
      { label: "Journey", href: "/about/journey/", desc: "Four phases: physician → privacy → decade → operator." },
      { label: "Credentials", href: "/about/credentials/", desc: "Medical foundation and operational track record." },
      { label: "Recognition", href: "/about/recognition/", desc: "Nobel Peace Prize, WEF, and sovereign stages." },
      { label: "Philosophy", href: "/about/philosophy/", desc: "The clinical method applied to organizations." },
    ],
  },
  {
    label: "Expertise",
    href: "/expertise/",
    icon: Layers,
    description: "Fifteen domains of sovereign-scale operational excellence.",
    sections: [
      { label: "Operational Excellence", href: "/expertise/operational-excellence/", desc: "Excellence as an engineered system." },
      { label: "Strategic Intelligence", href: "/expertise/strategic-intelligence/", desc: "Real-time intelligence and predictive analytics." },
      { label: "Political Strategy", href: "/expertise/political-strategy/", desc: "Voter intelligence and campaign engineering." },
      { label: "Governance & Policy", href: "/expertise/governance-public-policy/", desc: "Convert authority into measurable outcomes." },
      { label: "Crisis Management", href: "/expertise/crisis-management/", desc: "Crisis transformation at sovereign speed." },
      { label: "Privacy & Security", href: "/expertise/privacy-information-security/", desc: "Sovereign-grade encryption and zero-trust." },
    ],
  },
  {
    label: "Speaking",
    href: "/speaking/",
    icon: Mic,
    description: "Signature keynotes on operational excellence and leadership.",
    sections: [
      { label: "How Excellence Actually Runs", href: "/speaking/how-excellence-actually-runs/", desc: "The thesis keynote." },
      { label: "Leadership as Diagnosis", href: "/speaking/leadership-as-diagnosis/", desc: "The clinical method as leadership." },
      { label: "The 70% Project", href: "/speaking/the-70-percent-project/", desc: "The talent case from operations." },
      { label: "Build in the Dark", href: "/speaking/build-in-the-dark/", desc: "The operator's discipline." },
      { label: "The Triple Threat", href: "/speaking/the-triple-threat/", desc: "Medicine. Technology. Politics." },
      { label: "Women in the C-Suite", href: "/speaking/women-breaking-barriers/", desc: "Lessons from a decade of sovereign operations." },
    ],
  },
  {
    label: "Services",
    href: "/services/",
    icon: Briefcase,
    description: "Engagement modalities calibrated to the stakes.",
    sections: [
      { label: "Keynote Speaking", href: "/services/keynote-speaking/", desc: "Tailored keynotes for sovereign audiences." },
      { label: "Executive Advisory", href: "/services/executive-advisory/", desc: "Board service and operational advisory." },
      { label: "Operational Consulting", href: "/services/operational-consulting/", desc: "By introduction only." },
      { label: "Corporate Workshops", href: "/services/corporate-workshops/", desc: "Clinical-grade operational curricula." },
      { label: "Press & Media", href: "/services/press-media-commentary/", desc: "Expert commentary and analysis." },
      { label: "Writing & Thought Leadership", href: "/services/writing-thought-leadership/", desc: "The Operator's Notebook biweekly." },
    ],
  },
  {
    label: "Insights",
    href: "/insights/",
    icon: BookOpen,
    description: "The Operator's Notebook — biweekly notes on how excellent things run.",
    sections: [],
  },
];

export function MainHeader() {
  const [scrolled, setScrolled] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeMenu, setActiveMenu] = React.useState<string | null>(null);
  const pathname = usePathname();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setMobileOpen(false);
      setActiveMenu(null);
    }, 0);
    return () => clearTimeout(timer);
  }, [pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-sticky transition-all duration-300",
        scrolled
          ? "bg-background/85 backdrop-blur-xl border-b border-border/60 shadow-sm"
          : "bg-background/30 backdrop-blur-md border-b border-transparent",
      )}
      onMouseLeave={() => setActiveMenu(null)}
    >
      <div className="container-1720">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Brand */}
          <Link href="/" className="flex items-center gap-3 group shrink-0">
            <div className="relative size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 ring-2 ring-amber-gold-500/20">
              <Crown className="size-5 text-white" />
              <div className="absolute inset-0 rounded-xl bg-gradient-sovereign opacity-0 group-hover:opacity-50 blur-md transition-opacity" />
            </div>
            <div className="hidden sm:block">
              <div className="text-body-md font-bold tracking-tight leading-none text-foreground">Dr. Jyoti Kush</div>
              <div className="text-caption font-semibold text-amber-gold-600 dark:text-amber-gold-400 leading-none mt-1">COO · CryptoMize</div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => {
              const isActive = pathname === item.href || pathname.startsWith(item.href);
              return (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.sections.length > 0 && setActiveMenu(item.label)}
                >
                  <Link
                    href={item.href}
                    className={cn(
                      "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-body-sm font-semibold transition-colors",
                      isActive ? "text-red" : "text-foreground/85 hover:text-red",
                    )}
                  >
                    <item.icon className="size-3.5" />
                    {item.label}
                  </Link>
                  {item.sections.length > 0 && (
                    <AnimatePresence>
                      {activeMenu === item.label && (
                        <motion.div
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 6 }}
                          transition={{ duration: 0.15 }}
                          className="absolute left-0 top-full pt-2 z-popover"
                        >
                          <div className="w-[420px] glass-strong rounded-xl p-3 shadow-2xl">
                            <div className="px-3 py-2 mb-2">
                              <div className="text-overline text-muted-foreground">{item.label}</div>
                              <div className="text-body-sm font-semibold mt-0.5">{item.description}</div>
                            </div>
                            <div className="space-y-0.5">
                              {item.sections.map((s) => (
                                <Link
                                  key={s.href}
                                  href={s.href}
                                  className="block px-3 py-2.5 rounded-md hover:bg-red/5 hover:text-red transition-colors group/item"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="text-body-sm font-semibold">{s.label}</div>
                                      <div className="text-caption text-muted-foreground line-clamp-1">{s.desc}</div>
                                    </div>
                                    <ArrowUpRight className="size-3.5 opacity-0 group-hover/item:opacity-100 transition-opacity shrink-0 mt-0.5" />
                                  </div>
                                </Link>
                              ))}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button
              asChild
              size="default"
              className="hidden md:inline-flex h-10 px-5 bg-gradient-sovereign hover:scale-105 transition-transform shadow-lg font-bold"
            >
              <Link href="/contact/">
                <Phone className="size-4" />
                Book Dr. Jyoti
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden size-9"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl overflow-hidden"
          >
            <nav className="container-1720 py-4 space-y-1">
              {NAV.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  className="flex items-center gap-3 px-4 py-3 rounded-md hover:bg-red/5 transition-colors"
                >
                  <item.icon className="size-4 text-red" />
                  <div>
                    <div className="text-body-md font-semibold">{item.label}</div>
                    <div className="text-caption text-muted-foreground">{item.description}</div>
                  </div>
                </Link>
              ))}
              <Button
                asChild
                className="w-full mt-3 bg-gradient-sovereign"
              >
                <Link href="/contact/">
                  <Phone className="size-4" />
                  Book Dr. Jyoti Kush
                </Link>
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
